import { useState } from 'react';

export interface FeedbackPayload {
  useFor: string;
  useForSource: 'preset' | 'custom' | null;
  missing: string;
  trouble: string;
}

interface Props {
  onSubmit: (payload: FeedbackPayload) => void;
  onDismiss: () => void;
  onSnoozeToday: () => void;
}

const PRESETS = ['Birthday', 'Wedding', 'Sympathy', 'Game card', 'Prototype'];
const MAX_LEN = 240;

export function FeedbackPrompt({ onSubmit, onDismiss, onSnoozeToday }: Props) {
  const [useFor, setUseFor] = useState('');
  // Whether the current useFor value matches a preset selection (vs. typed).
  // Set when a preset chip is clicked; cleared the moment the user edits the input.
  const [useForFromPreset, setUseForFromPreset] = useState(false);
  const [missing, setMissing] = useState('');
  const [trouble, setTrouble] = useState('');

  const trimAll = () => ({
    useFor: useFor.trim(),
    missing: missing.trim(),
    trouble: trouble.trim(),
  });

  const trimmed = trimAll();
  const anyFilled = Boolean(trimmed.useFor || trimmed.missing || trimmed.trouble);

  const handlePresetClick = (preset: string) => {
    setUseFor(preset);
    setUseForFromPreset(true);
  };

  const handleSubmit = () => {
    const t = trimAll();
    if (!t.useFor && !t.missing && !t.trouble) return;
    onSubmit({
      useFor: t.useFor,
      useForSource: t.useFor
        ? useForFromPreset && PRESETS.map((p) => p.toLowerCase()).includes(t.useFor.toLowerCase())
          ? 'preset'
          : 'custom'
        : null,
      missing: t.missing,
      trouble: t.trouble,
    });
  };

  return (
    <div
      className="feedback-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="feedback-title"
      onClick={onDismiss}
    >
      <div className="feedback-modal" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="feedback-close"
          onClick={onDismiss}
          aria-label="Close"
          title="Close (will ask again on next download)"
        >
          ✕
        </button>

        <span className="feedback-pill">Quick questions · optional</span>

        <h3 id="feedback-title" className="feedback-title">
          Help me improve this tool
        </h3>

        <p className="feedback-meta">
          Anonymous — only your answers and a coarse country code are stored. No
          images, no IPs, no identifiers. All three questions are optional;
          fill any (or none) and hit Submit.
        </p>

        <div className="feedback-q">
          <label className="feedback-q-label" htmlFor="fb-usefor">
            1. What are you using this card for?
          </label>
          <div className="feedback-presets">
            {PRESETS.map((preset) => (
              <button
                key={preset}
                type="button"
                className={`feedback-opt${useFor.toLowerCase() === preset.toLowerCase() ? ' active' : ''}`}
                onClick={() => handlePresetClick(preset)}
                title="Click to fill the field below — you still need to click Submit"
              >
                {preset}
              </button>
            ))}
          </div>
          <input
            id="fb-usefor"
            type="text"
            value={useFor}
            onChange={(e) => {
              setUseFor(e.target.value);
              setUseForFromPreset(false);
            }}
            placeholder="Click a chip above or type your own (e.g. baby shower)"
            maxLength={MAX_LEN}
            className="feedback-input"
          />
        </div>

        <div className="feedback-q">
          <label className="feedback-q-label" htmlFor="fb-missing">
            2. Any features you wish this had?
          </label>
          <textarea
            id="fb-missing"
            value={missing}
            onChange={(e) => setMissing(e.target.value)}
            placeholder="e.g. text overlay tool, photo collage mode, A6 size…"
            maxLength={MAX_LEN}
            className="feedback-input feedback-textarea"
            rows={2}
          />
        </div>

        <div className="feedback-q">
          <label className="feedback-q-label" htmlFor="fb-trouble">
            3. Did you hit any trouble or bugs?
          </label>
          <textarea
            id="fb-trouble"
            value={trouble}
            onChange={(e) => setTrouble(e.target.value)}
            placeholder="e.g. preview disappeared after I changed paper size"
            maxLength={MAX_LEN}
            className="feedback-input feedback-textarea"
            rows={2}
          />
        </div>

        <div className="feedback-actions">
          <button
            type="button"
            className="feedback-submit"
            onClick={handleSubmit}
            disabled={!anyFilled}
          >
            Submit
          </button>
        </div>

        <div className="feedback-footer">
          <button type="button" className="feedback-snooze" onClick={onSnoozeToday}>
            Don't ask again today
          </button>
        </div>
      </div>
    </div>
  );
}
